import { useState, useEffect } from 'react';
import { Brain, AlertTriangle, Target, Wallet } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LineChart, Line, CartesianGrid } from 'recharts';
import ModuleContainer from '../components/layout/ModuleContainer.jsx';
import { usePage } from '../contexts/PageContext.jsx';

// Sprint 22.7.10 (S-B): KAIROS per-domain prediction performance.
// Splits hit_rate / calibration / Brier by asset domain so the operator can
// see WHERE predictions are good vs bad — the instrument for grading whether a
// domain's miscalibration is a KAIROS-reasoning problem or an input-data one.
// Reads GET /api/kairos/performance-by-domain (computed by kairosMetricsService
// + predictionDomainResolver). Under-sampled domains (<5 resolved) render as
// "ungraded", never a misleading percentage.

const DOMAIN_LABELS = {
  fx: 'Currency (FX)',
  commodity: 'Commodity (Oil/Gold)',
  crypto: 'Crypto',
  equity: 'Equities',
  volatility: 'Volatility',
  rates: 'Rates (Fixed Income)',
  macro: 'Macro / Event',
};

const WINDOWS = [
  { key: 'all_time', label: 'All-time' },
  { key: 'recent_30d', label: 'Last 30d' },
];

function hitColor(hr) {
  if (hr == null) return '#6b7280';        // muted (ungraded)
  if (hr >= 0.55) return '#10b981';         // green
  if (hr >= 0.50) return '#f59e0b';         // amber
  return '#ef4444';                          // red
}

function pct(x) {
  return x == null ? '—' : `${(x * 100).toFixed(1)}%`;
}

function usd(x) {
  if (x == null) return '—';
  const sign = x < 0 ? '-' : '+';
  return `${sign}$${Math.abs(x).toLocaleString()}`;
}

// Sprint 22.7.35: Shadow P&L — "if you followed every call" ledger.
// Reads GET /api/shadow/report (kairosShadowService.getShadowReport). Cash
// calls and voided unit-mismatch artifacts are excluded server-side; friction
// (20bps round trip per flip) is shown next to gross so churn is priced.
function ShadowPnlSection() {
  const [rep, setRep] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const r = await fetch('/api/shadow/report');
        if (!r.ok) { if (alive) setErr(`HTTP ${r.status}`); return; }
        const d = await r.json();
        if (alive) { setRep(d); setErr(null); }
      } catch (e) { if (alive) setErr(e.message); }
    }
    load();
    const iv = setInterval(load, 300_000); // 5 min — realized P&L moves hourly at most
    return () => { alive = false; clearInterval(iv); };
  }, []);

  if (err) return <ModuleContainer title="Shadow P&L"><div className="text-xs text-red-400 font-mono">Failed to load: {err}</div></ModuleContainer>;
  if (!rep) return null;

  const at = rep.positions?.all_time || {};
  const r30 = rep.positions?.recent_30d || {};
  const curve = rep.equity_curve || [];
  const convRows = Object.entries(at.by_conviction || {}).sort((a, b) => (b[0] === 'none' ? -1 : a[0] === 'none' ? 1 : Number(b[0]) - Number(a[0])));
  const dq = rep.data_quality || {};

  const stat = (label, value, color) => (
    <div className="bg-korinth-bg/50 border border-korinth-border/30 rounded-lg p-2.5">
      <div className="text-[9px] font-mono uppercase text-korinth-muted">{label}</div>
      <div className={`text-sm font-bold font-mono ${color || 'text-white'}`}>{value}</div>
    </div>
  );

  return (
    <ModuleContainer
      title={<span className="flex items-center gap-2"><Wallet className="w-4 h-4 text-korinth-accent" />Shadow P&L — if you followed every call</span>}
      subtitle="Each closed call = one independent $100K account · gross vs friction-adjusted (20bps/flip) · go-live gate input G4/G5"
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
          {stat('Gross P&L (all-time)', usd(at.total_realized_usd), (at.total_realized_usd || 0) >= 0 ? 'text-korinth-accent' : 'text-korinth-danger')}
          {stat('Friction-adjusted', usd(at.friction_adjusted_usd), (at.friction_adjusted_usd || 0) >= 0 ? 'text-korinth-accent' : 'text-korinth-danger')}
          {stat('Win rate', at.win_rate_pct != null ? `${at.win_rate_pct}%` : '—')}
          {stat('Closed calls', at.n_closed ?? 0)}
          {stat('Median hold', at.median_hold_hours != null ? `${at.median_hold_hours}h` : '—', at.median_hold_hours != null && at.median_hold_hours < 72 ? 'text-korinth-warning' : null)}
          {stat('30d friction-adj.', usd(r30.friction_adjusted_usd), (r30.friction_adjusted_usd || 0) >= 0 ? 'text-korinth-accent' : 'text-korinth-danger')}
        </div>

        {curve.length > 1 && (
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={curve} margin={{ top: 6, right: 8, left: -8, bottom: 0 }}>
                <CartesianGrid stroke="#1f2937" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="t" tick={{ fontSize: 9, fill: '#6b7280' }} axisLine={false} tickLine={false} tickFormatter={(t) => String(t).slice(5, 10)} minTickGap={40} />
                <YAxis tick={{ fontSize: 9, fill: '#6b7280' }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip contentStyle={{ background: '#111827', border: '1px solid #1f2937', borderRadius: 8, fontSize: 11 }} formatter={(v) => [usd(v), 'Cumulative realized']} labelFormatter={(t) => `${String(t).slice(0, 16)} UTC`} />
                <Line type="stepAfter" dataKey="cum_usd" stroke="#10b981" strokeWidth={1.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {convRows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-[11px] font-mono">
              <thead>
                <tr className="text-korinth-muted border-b border-korinth-border/30">
                  <th className="text-left py-1.5 px-2">Conviction</th>
                  <th className="text-right py-1.5 px-2">n</th>
                  <th className="text-right py-1.5 px-2">Win rate</th>
                  <th className="text-right py-1.5 px-2">P&L</th>
                </tr>
              </thead>
              <tbody>
                {convRows.map(([bucket, b]) => (
                  <tr key={bucket} className="border-b border-korinth-border/10">
                    <td className="py-1.5 px-2 text-white">{bucket === 'none' ? 'unscored' : `${bucket}/10`}</td>
                    <td className="py-1.5 px-2 text-right text-korinth-muted">{b.n}</td>
                    <td className="py-1.5 px-2 text-right text-korinth-text">{b.n > 0 ? `${Math.round(b.wins / b.n * 100)}%` : '—'}</td>
                    <td className={`py-1.5 px-2 text-right font-bold ${b.pnl_usd >= 0 ? 'text-korinth-accent' : 'text-korinth-danger'}`}>{usd(Math.round(b.pnl_usd))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="text-[10px] font-mono text-korinth-muted border-t border-korinth-border/30 pt-2">
          Exit reasons: {Object.entries(at.by_exit_reason || {}).map(([k, v]) => `${k} ${v}`).join(' · ') || 'none'} ·
          excluded: {dq.cash_positions_excluded ?? 0} cash calls, {dq.voided_prediction_artifacts ?? 0} voided unit-mismatch artifacts ·
          {dq.open_positions ?? 0} open, last mark {dq.last_mark_time ? `${String(dq.last_mark_time).slice(0, 16)} UTC` : '—'}
        </p>
      </div>
    </ModuleContainer>
  );
}

export default function KairosPerformancePage() {
  const { setPage } = usePage();
  useEffect(() => { setPage('kairos-performance'); }, []);

  const [data, setData] = useState(null);
  const [win, setWin] = useState('all_time');
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const r = await fetch('/api/kairos/performance-by-domain');
        if (!r.ok) { if (alive) setErr(`HTTP ${r.status}`); return; }
        const d = await r.json();
        if (alive) { setData(d); setErr(null); }
      } catch (e) { if (alive) setErr(e.message); }
    }
    load();
    const iv = setInterval(load, 120_000); // 2 min
    return () => { alive = false; clearInterval(iv); };
  }, []);

  const rows = data?.windows?.[win] || [];
  const totalResolved = rows.reduce((a, d) => a + (d.sample_size || 0), 0);
  const gradedRows = rows.filter((d) => d.graded);
  const chartData = gradedRows.map((d) => ({
    domain: DOMAIN_LABELS[d.domain] || d.domain,
    hit: Math.round((d.hit_rate || 0) * 1000) / 10, // percentage
    fill: hitColor(d.hit_rate),
  }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <Brain className="w-6 h-6 text-korinth-accent" />
        <h1 className="text-2xl font-bold text-white">KAIROS Performance by Domain</h1>
        <span className="text-korinth-muted text-sm">
          Where predictions are good vs bad — per asset domain
        </span>
      </div>

      {/* Window toggle + stats strip */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex gap-1 bg-korinth-bg/50 border border-korinth-border/30 rounded-lg p-1">
          {WINDOWS.map((w) => (
            <button
              key={w.key}
              onClick={() => setWin(w.key)}
              className={`px-3 py-1 text-xs rounded-md transition-colors ${
                win === w.key
                  ? 'bg-korinth-accent/20 text-korinth-accent font-semibold'
                  : 'text-korinth-muted hover:text-korinth-text'
              }`}
            >
              {w.label}
            </button>
          ))}
        </div>
        <div className="text-[11px] font-mono text-korinth-muted">
          <Target className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
          {totalResolved} resolved predictions · sample floor n≥{data?.sample_floor ?? 5}
        </div>
      </div>

      {err && (
        <div className="text-xs text-red-400 font-mono">Failed to load: {err}</div>
      )}

      {/* Hit-rate bar chart (graded domains only) */}
      <ModuleContainer title="Hit rate by domain" subtitle="Graded domains only (n ≥ sample floor)">
        <div className="h-52">
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: -16, bottom: 4 }}>
                <XAxis dataKey="domain" tick={{ fontSize: 9, fill: '#6b7280' }} axisLine={false} tickLine={false} interval={0} angle={-12} textAnchor="end" height={42} />
                <YAxis tick={{ fontSize: 9, fill: '#6b7280' }} axisLine={false} tickLine={false} domain={[0, 100]} unit="%" />
                <Tooltip
                  contentStyle={{ background: '#111827', border: '1px solid #1f2937', borderRadius: 8, fontSize: 11 }}
                  formatter={(v) => [`${v.toFixed(1)}%`, 'Hit rate']}
                  cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                />
                <Bar dataKey="hit" radius={[3, 3, 0, 0]}>
                  {chartData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-full text-[11px] text-korinth-muted">
              No domain has enough resolved predictions to grade yet.
            </div>
          )}
        </div>
      </ModuleContainer>

      {/* Per-domain table (all domains, incl. ungraded) */}
      <ModuleContainer title="Per-domain breakdown" subtitle="Hit rate, calibration error, Brier, sample size">
        <div className="overflow-x-auto">
          <table className="w-full text-[11px] font-mono">
            <thead>
              <tr className="text-korinth-muted border-b border-korinth-border/30">
                <th className="text-left py-1.5 px-2">Domain</th>
                <th className="text-right py-1.5 px-2">Hit rate</th>
                <th className="text-right py-1.5 px-2">Calib. error</th>
                <th className="text-right py-1.5 px-2">Brier</th>
                <th className="text-right py-1.5 px-2">Correct / Partial / Wrong</th>
                <th className="text-right py-1.5 px-2">n</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.domain} className="border-b border-korinth-border/10 hover:bg-korinth-bg/50">
                  <td className="py-1.5 px-2 text-white">{DOMAIN_LABELS[d.domain] || d.domain}</td>
                  <td className="py-1.5 px-2 text-right font-bold" style={{ color: hitColor(d.hit_rate) }}>
                    {d.graded ? pct(d.hit_rate) : <span className="text-korinth-muted font-normal">ungraded</span>}
                  </td>
                  <td className="py-1.5 px-2 text-right text-korinth-text">
                    {d.graded && d.calibration_error != null ? `${(d.calibration_error * 100).toFixed(1)}pp` : '—'}
                  </td>
                  <td className="py-1.5 px-2 text-right text-korinth-text">
                    {d.graded && d.brier != null ? d.brier.toFixed(3) : '—'}
                  </td>
                  <td className="py-1.5 px-2 text-right text-korinth-muted">
                    {d.n_correct} / {d.n_partial} / {d.n_wrong}
                  </td>
                  <td className="py-1.5 px-2 text-right text-korinth-muted">{d.sample_size}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={6} className="py-3 px-2 text-center text-korinth-muted">No data.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </ModuleContainer>

      {/* Sprint 22.7.35: Shadow P&L ledger (go-live gate input) */}
      <ShadowPnlSection />

      {/* Honest notes (zero / under-sampled domains) */}
      {data?.notes?.length > 0 && (
        <ModuleContainer title="Data-quality notes" subtitle="Domains that cannot be graded yet">
          <ul className="space-y-1.5">
            {data.notes.map((n, i) => (
              <li key={i} className="flex items-start gap-2 text-[11px] text-korinth-muted">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 text-korinth-amber shrink-0" />
                <span>{n}</span>
              </li>
            ))}
          </ul>
        </ModuleContainer>
      )}
    </div>
  );
}
